import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { beginGoogleLogin } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ShieldCheck, Zap, Clock, MapPin, Activity, Stethoscope, Syringe, HeartPulse,
  ArrowRight, CheckCircle2, QrCode, Wallet, Users, Building2, Phone
} from "lucide-react";

const specialties = [
  { name: "Duty Medical Officer", icon: Stethoscope, tag: "DMO", desc: "Ward & OPD coverage, 8–24h shifts", rate: "₹700–1,000/hr" },
  { name: "Intensivist", icon: HeartPulse, tag: "ICU", desc: "Critical care, ventilator management", rate: "₹1,500–2,200/hr" },
  { name: "Emergency Medicine", icon: Activity, tag: "ER", desc: "Emergency room, trauma & triage", rate: "₹1,000–1,500/hr" },
  { name: "Anaesthesiologist", icon: Syringe, tag: "OT", desc: "OT & procedural anaesthesia cover", rate: "₹1,400–1,800/hr" },
];

const hospitals = [
  "Apollo Hospitals", "KIMS Secunderabad", "Yashoda Somajiguda",
  "Continental Gachibowli", "Rainbow Children's", "Care HITEC City", "Star Hospitals",
];

export default function Landing() {
  const { user } = useAuth();
  const goApp = () => (user ? (window.location.href = "/dashboard") : beginGoogleLogin());

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-lg bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#0B1528] flex items-center justify-center">
              <HeartPulse className="w-4 h-4 text-sky-400" />
            </div>
            <span className="font-extrabold text-lg tracking-tight">MedShift</span>
            <Badge variant="outline" className="ml-2 border-sky-200 text-sky-700 bg-sky-50 text-[10px]">Hyderabad · Live</Badge>
          </div>
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-600 font-medium">
            <a href="#how" className="hover:text-slate-900">How it works</a>
            <a href="#hospitals" className="hover:text-slate-900">For Hospitals</a>
            <a href="#doctors" className="hover:text-slate-900">For Doctors</a>
            <a href="#specialties" className="hover:text-slate-900">Specialties</a>
          </nav>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" data-testid="header-login-btn" onClick={goApp}>Sign in</Button>
            <Button size="sm" className="bg-[#0B1528] hover:bg-[#0B1528]/90 rounded-full px-4" data-testid="header-cta-btn" onClick={goApp}>
              Find a Doctor Now
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Badge className="bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-50 rounded-full font-mono text-[11px] uppercase tracking-widest">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-2"></span> Verified doctors · Live in Hyderabad
            </Badge>
            <h1 className="mt-5 font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-slate-950">
              Fill critical doctor shifts.<br/>
              <span className="grad-navy">Without the phone calls.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-xl leading-relaxed">
              MedShift connects hospitals with verified DMO, Emergency, Intensivist and Anaesthesia doctors for hourly and on-demand duties — with QR attendance, escrow payouts and instant replacement.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" className="bg-sky-600 hover:bg-sky-700 rounded-full px-6 h-12" data-testid="hero-find-doctor-btn" onClick={goApp}>
                Find a Doctor Now <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-full px-6 h-12 border-slate-300" data-testid="hero-join-doctor-btn" onClick={goApp}>
                Join as a Doctor
              </Button>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-6 max-w-lg">
              {[
                ["12 min", "Avg match time"],
                ["96.4%", "Fulfilment rate"],
                ["100%", "TGMC verified"],
              ].map(([v, l]) => (
                <div key={l}>
                  <div className="font-mono text-2xl font-bold text-slate-900">{v}</div>
                  <div className="text-xs text-slate-500 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Live dashboard preview */}
          <div className="lg:col-span-5" data-testid="hero-preview">
            <Card className="med-shadow border-slate-200 rounded-2xl overflow-hidden bg-white">
              <div className="bg-[#0B1528] text-white p-5">
                <div className="flex items-center justify-between text-xs font-mono uppercase tracking-widest text-sky-300">
                  <span>Live · Apollo Jubilee Hills</span>
                  <span className="flex items-center gap-1.5"><span className="relative text-emerald-400"><span className="pulse-dot"></span><span className="w-2 h-2 rounded-full bg-emerald-400 block"></span></span> Active</span>
                </div>
                <h3 className="mt-2 text-xl font-bold">Emergency ICU Intensivist</h3>
                <p className="text-sky-200 text-sm mt-0.5">Tonight · 20:00 → 08:00 · ₹1,800/hr</p>
              </div>
              <div className="p-5 space-y-4">
                {[
                  { name: "Dr. Rohan Verma", spec: "Intensivist · 8y", dist: "3.2 km", rate: "₹1,800", score: 97 },
                  { name: "Dr. Priya Nair", spec: "DNB Critical Care · 6y", dist: "5.4 km", rate: "₹1,600", score: 93 },
                  { name: "Dr. Meera Reddy", spec: "MD Anaesthesia · 9y", dist: "6.1 km", rate: "₹1,700", score: 91 },
                ].map((d) => (
                  <div key={d.name} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-sky-300 transition">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-sky-700 text-white flex items-center justify-center font-bold text-sm">
                      {d.name.split(" ")[1][0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-sm">{d.name}</span>
                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                        {d.spec} <span className="text-slate-300">·</span> <MapPin className="w-3 h-3" /> {d.dist}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-mono text-slate-500">Match</div>
                      <div className="font-mono font-bold text-emerald-600">{d.score}%</div>
                    </div>
                  </div>
                ))}
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-xl h-11" data-testid="preview-book-btn">
                  <Zap className="w-4 h-4 mr-1" /> Book best match instantly
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-y border-slate-200 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-xs uppercase tracking-widest font-semibold text-slate-500 font-mono">
          <span>Trusted by:</span>
          {hospitals.map(h => <span key={h}>{h}</span>)}
        </div>
      </section>

      {/* Specialties */}
      <section id="specialties" className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-sky-600 font-semibold">Coverage</div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight">Four core specialties. Fully vetted.</h2>
          <p className="mt-3 text-slate-600">Every doctor is TGMC-registered, credential-verified and reliability-rated. No cold calls, no scrambling at midnight.</p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {specialties.map(s => (
            <Card key={s.name} className="p-6 rounded-2xl border-slate-200 hover:border-sky-300 hover:-translate-y-0.5 transition med-shadow" data-testid={`specialty-${s.tag}`}>
              <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center">
                <s.icon className="w-5 h-5" />
              </div>
              <div className="mt-4 text-[11px] font-mono uppercase tracking-widest text-slate-400">{s.tag}</div>
              <h3 className="text-lg font-bold mt-1">{s.name}</h3>
              <p className="text-sm text-slate-600 mt-1">{s.desc}</p>
              <div className="mt-4 text-sm font-mono font-semibold text-slate-900">{s.rate}</div>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12">
          <div id="hospitals">
            <Badge className="bg-white border border-slate-200 text-slate-700 rounded-full">For Hospitals</Badge>
            <h3 className="mt-3 text-3xl font-extrabold tracking-tight">Post a shift. Fill it in minutes.</h3>
            <ol className="mt-6 space-y-4">
              {[
                { i: Building2, t: "Post requirement", d: "Specialty, department, timing, rate, urgency. Emergency? One tap." },
                { i: Users, t: "See ranked matches", d: "Verified doctors sorted by distance, reliability score and specialty fit." },
                { i: CheckCircle2, t: "Book & auto-notify", d: "Doctor accepts on their phone. QR attendance keeps everything auditable." },
                { i: Wallet, t: "Approve & pay", d: "Invoice generated automatically. Escrow releases to doctor UPI same-day." },
              ].map((s, idx) => (
                <li key={s.t} className="flex gap-4">
                  <div className="w-10 h-10 rounded-xl bg-sky-600 text-white flex items-center justify-center font-mono font-bold text-sm">0{idx+1}</div>
                  <div>
                    <div className="font-semibold flex items-center gap-2"><s.i className="w-4 h-4 text-sky-600" />{s.t}</div>
                    <div className="text-sm text-slate-600 mt-0.5">{s.d}</div>
                  </div>
                </li>
              ))}
            </ol>
            <Button className="mt-8 bg-[#0B1528] hover:bg-[#0B1528]/90 rounded-full px-6 h-11" data-testid="how-hospital-cta" onClick={goApp}>
              Post a requirement <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div id="doctors">
            <Badge className="bg-white border border-slate-200 text-slate-700 rounded-full">For Doctors</Badge>
            <h3 className="mt-3 text-3xl font-extrabold tracking-tight">Legitimate duties. Fair pay. Same-day.</h3>
            <ol className="mt-6 space-y-4">
              {[
                { i: ShieldCheck, t: "Verify credentials", d: "Upload TGMC / MBBS / PG certificates. Reviewed by clinical team." },
                { i: Clock, t: "Set availability", d: "Mark hours, preferred areas, emergency-only or open shifts." },
                { i: QrCode, t: "Accept & check-in", d: "One tap accept. QR check-in on arrival, check-out on completion." },
                { i: Wallet, t: "Get paid fast", d: "Escrow-secured earnings released to your UPI after hospital approval." },
              ].map((s, idx) => (
                <li key={s.t} className="flex gap-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-mono font-bold text-sm">0{idx+1}</div>
                  <div>
                    <div className="font-semibold flex items-center gap-2"><s.i className="w-4 h-4 text-emerald-600" />{s.t}</div>
                    <div className="text-sm text-slate-600 mt-0.5">{s.d}</div>
                  </div>
                </li>
              ))}
            </ol>
            <Button className="mt-8 bg-emerald-600 hover:bg-emerald-700 rounded-full px-6 h-11" data-testid="how-doctor-cta" onClick={goApp}>
              Join as a Doctor <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* Emergency */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="rounded-3xl bg-[#0B1528] text-white p-10 lg:p-14 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-sky-500/20 blur-3xl"></div>
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <Badge className="bg-red-500/20 text-red-200 border border-red-400/30 rounded-full font-mono uppercase tracking-widest text-[10px]">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 mr-2 animate-pulse"></span> Doctor Now
              </Badge>
              <h3 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight">Need a doctor in the next 30 minutes?</h3>
              <p className="mt-3 text-sky-100/80 max-w-lg">MedShift instantly broadcasts your emergency to every verified, available specialist within radius. First accept wins.</p>
              <Button size="lg" className="mt-6 bg-red-500 hover:bg-red-600 rounded-full px-6 h-12" data-testid="emergency-cta" onClick={goApp}>
                <Phone className="w-4 h-4 mr-2" /> Trigger Emergency Broadcast
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                ["<15 min", "Avg emergency response"],
                ["8 areas", "Hyderabad coverage"],
                ["Auto", "Replacement dispatch"],
                ["24/7", "Clinical support"],
              ].map(([v, l]) => (
                <div key={l} className="rounded-2xl bg-white/5 border border-white/10 p-5">
                  <div className="font-mono text-2xl font-bold">{v}</div>
                  <div className="text-xs text-sky-200/70 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-[#0B1528] flex items-center justify-center">
                <HeartPulse className="w-3.5 h-3.5 text-sky-400" />
              </div>
              <span className="font-extrabold">MedShift</span>
            </div>
            <p className="mt-3 text-slate-500 leading-relaxed">The operating system for on-demand hospital clinical staffing.</p>
          </div>
          <div>
            <div className="font-semibold text-slate-900">Platform</div>
            <ul className="mt-3 space-y-2 text-slate-600">
              <li>For Hospitals</li><li>For Doctors</li><li>Specialties</li><li>Pricing</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-slate-900">Company</div>
            <ul className="mt-3 space-y-2 text-slate-600">
              <li>About</li><li>Contact</li><li>Careers</li><li>Press</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-slate-900">Legal</div>
            <ul className="mt-3 space-y-2 text-slate-600">
              <li>Privacy Policy</li><li>Terms of Service</li><li>Grievance</li><li>Emergency Hotline</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200 py-5 text-center text-xs text-slate-500">
          © 2026 MedShift. Verified Doctors. When Hospitals Need Them.
        </div>
      </footer>
    </div>
  );
}
